import React, { useEffect, useRef, useState } from "react";
import { X, Mic, Send, Square } from "lucide-react";
import Orb from "@/components/Orb";
import { api } from "@/lib/api";

// Phrases that end the conversation (e.g. "thanks, that's all for now").
const SIGNOFF = /\b(that'?s all|thats all|all for now|that is all|that'?ll be all|i'?m done|im done|we'?re done|good ?bye|bye andre|see you later|nothing else|that'?s everything|thats everything|that'?s it for now)\b/i;
// Quick voice confirmations of the most recent capture card (person / vault_note).
const APPROVE = /^(?:please\s+)?(?:yes,?\s+)?(?:go ahead and\s+|please\s+)?(save it|save that|save them|save it now|approve(?:\s+(?:that|it))?|do it|yes,?\s+(?:save|approve|do it)|confirm|looks good,?\s*save it|yep,?\s*save it|yeah,?\s*save it)\.?$/i;
const SKIP = /^(?:no,?\s+)?(skip(?:\s+(?:that|it))?|dismiss(?:\s+(?:that|it))?|cancel that|never ?mind|forget it|drop it)\.?$/i;
const FAREWELL = "Anytime. I'm right here whenever you need me.";
// Give the speaker a calm pause to finish their thought.
const SILENCE_MS = 3000;
// Max length of a single push-to-talk recording (safety cap).
const MAX_RECORD_MS = 60_000;

// --- Push-to-talk voice-activity detection (VAD) tuning -----------------
// RMS amplitude (0..1) above which we consider the user to be speaking.
const SPEECH_RMS = 0.018;
// Speech must persist this long before it counts (rejects clicks/taps).
const MIN_SPEECH_MS = 300;
// After speech, this much silence auto-ends the turn (a natural pause).
const POST_SPEECH_SILENCE_MS = 1400;
// If we're listening and hear NO speech at all for this long, stop quietly.
const NO_SPEECH_TIMEOUT_MS = 9000;

// Detect browsers without webkitSpeechRecognition.
function hasNativeSR() {
  if (typeof window === "undefined") return false;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

// iOS (iPhone/iPad/iPod) — including iPadOS which masquerades as "MacIntel".
// iOS Safari DOES expose webkitSpeechRecognition since iOS 14.5, but it is
// unreliable for hands-free use (no real continuous mode and start() must run
// inside a fresh user gesture). So on iOS we always use the MediaRecorder +
// Whisper push-to-talk path instead of SpeechRecognition.
function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
}

// Choose the capture mode: push-to-talk on iOS (or any browser without SR),
// continuous SpeechRecognition everywhere else (desktop / Android Chrome).
function pickMode() {
  if (isIOS()) return "push";
  return hasNativeSR() ? "sr" : "push";
}

// Tiny silent MP3 (~0.1s) used to "unlock" the iOS audio output. iOS Safari
// blocks `audio.play()` outside a user gesture; playing this once during the
// opening tap puts the same <audio> element into a "permitted" state so later
// .play() calls (after our async TTS fetch) are allowed.
const SILENT_MP3 =
  "data:audio/mp3;base64,/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

// Pick a MediaRecorder MIME type that this browser can actually produce.
// iOS Safari speaks audio/mp4 (m4a); Chrome (Android + desktop) prefers webm.
function pickRecorderMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const tries = [
    "audio/mp4",            // iOS Safari
    "audio/mp4;codecs=mp4a.40.2",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
  ];
  for (const t of tries) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* ignore */ }
  }
  return "";
}

/**
 * Hands-free voice mode. Two paths:
 *   • Desktop/Android Chrome: native webkitSpeechRecognition (continuous mic).
 *   • iOS Safari (+ any browser without SR): MediaRecorder + Whisper with
 *     voice-activity detection — tap once to start, then it auto-sends on a
 *     natural pause and auto-resumes listening after the reply, so it's a true
 *     hands-free continuous conversation (tap the orb to send early).
 */
export default function VoiceOverlay({ open, onClose, onSubmit, assistantName = "Andre" }) {
  const [state, setState] = useState("idle"); // idle | listening | recording | thinking | speaking
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  const [supported, setSupported] = useState(true);
  const [mode] = useState(() => pickMode());

  const recRef = useRef(null);
  const audioRef = useRef(null);
  const activeRef = useRef(false);
  const busyRef = useRef(false);
  const fatalRef = useRef(false);
  const silenceRef = useRef(null);
  // Push-to-talk plumbing.
  const mediaRecRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const manualStopRef = useRef(false);  // user tapped "send"
  const autoStopRef = useRef(false);    // VAD detected an end-of-turn pause
  const giveUpRef = useRef(false);      // heard nothing → stop quietly
  // Voice-activity detection over the live mic stream.
  const audioCtxRef = useRef(null);
  const vadSourceRef = useRef(null);
  const analyserRef = useRef(null);
  const vadRafRef = useRef(null);
  const heardSpeechRef = useRef(false);
  const vadSilenceStartRef = useRef(0);
  // Consecutive empty/unintelligible captures — bounded so a noisy room can't
  // loop the mic forever, but one bad parse won't break the hands-free flow.
  const emptyTurnsRef = useRef(0);
  // The single <audio> element reused for all TTS playback so iOS keeps it
  // "unlocked" across awaits. Created lazily on first open.
  const sinkRef = useRef(null);
  // TTS request controller + a monotonically increasing token so a stalled or
  // superseded reply can never play over (or repeat under) the current one.
  const ttsAbortRef = useRef(null);
  const speakSeqRef = useRef(0);

  function clearSilence() {
    if (silenceRef.current) { clearTimeout(silenceRef.current); silenceRef.current = null; }
  }

  function stopRec() {
    clearSilence();
    const r = recRef.current;
    if (!r) return;
    try { r.onresult = null; r.onerror = null; r.onend = null; } catch { /* ignore */ }
    try { r.abort(); } catch { /* ignore */ }
    recRef.current = null;
  }

  // Stop any spoken reply immediately and invalidate in-flight TTS so a slow or
  // superseded request can't play late (the cause of the "robotic voice that
  // repeats itself" after a stall). Bumping the sequence token makes every
  // pending playSpeech callback see itself as stale and bail.
  function stopSpeaking() {
    speakSeqRef.current += 1;
    try { ttsAbortRef.current?.abort(); } catch { /* ignore */ }
    ttsAbortRef.current = null;
    try { sinkRef.current?.pause(); } catch { /* ignore */ }
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
  }

  // ---------------------------------------------------------------------
  // iOS audio unlock — play a silent MP3 during the opening user gesture so
  // subsequent audio.play() calls after our async TTS fetch are allowed.
  // The same <audio> element is reused for every spoken reply.
  // ---------------------------------------------------------------------
  function ensureAudioSink() {
    if (sinkRef.current) return sinkRef.current;
    const a = new Audio();
    a.preload = "auto";
    a.crossOrigin = "anonymous";
    // iOS needs playsInline so it doesn't try to take over the fullscreen
    // video UI for an <audio> element.
    a.setAttribute("playsinline", "");
    a.setAttribute("webkit-playsinline", "");
    sinkRef.current = a;
    return a;
  }

  async function unlockAudio() {
    const a = ensureAudioSink();
    try {
      a.src = SILENT_MP3;
      a.muted = true;
      a.volume = 0;
      const p = a.play();
      if (p && typeof p.then === "function") await p.catch(() => {});
      a.pause();
      a.currentTime = 0;
      a.muted = false;
      a.volume = 1;
    } catch { /* ignore — fallback path will still try */ }
  }

  function playSpeech(text) {
    return new Promise((resolve) => {
      // Cancel anything still speaking / fetching from a previous turn first.
      stopSpeaking();
      const mySeq = speakSeqRef.current;
      setState("speaking");
      let settled = false;
      const finish = () => { if (!settled) { settled = true; resolve(); } };
      // True once this reply has been superseded or the overlay closed.
      const stale = () => speakSeqRef.current !== mySeq || !activeRef.current;
      const fallback = () => {
        if (stale()) { finish(); return; }
        try {
          window.speechSynthesis.cancel();
          const u = new SpeechSynthesisUtterance(text);
          u.onend = finish;
          u.onerror = finish;
          window.speechSynthesis.speak(u);
        } catch { finish(); }
      };
      (async () => {
        // Up to 2 attempts: a quick transient failure retries once before we
        // drop to the robotic browser voice; a true timeout does not.
        for (let attempt = 0; attempt < 2; attempt++) {
          if (stale()) { finish(); return; }
          const ctrl = new AbortController();
          ttsAbortRef.current = ctrl;
          const started = Date.now();
          const timer = setTimeout(() => { try { ctrl.abort(); } catch { /* ignore */ } }, 14000);
          try {
            const res = await api.post("/voice/tts", { text },
              { responseType: "blob", signal: ctrl.signal, timeout: 14000 });
            clearTimeout(timer);
            if (stale()) { finish(); return; }
            const url = URL.createObjectURL(res.data);
            const a = ensureAudioSink();
            audioRef.current = a;
            // Reuse the same element to keep iOS' "unlocked" state.
            a.onended = () => { URL.revokeObjectURL(url); finish(); };
            a.onerror = () => { URL.revokeObjectURL(url); fallback(); };
            a.src = url;
            a.muted = false;
            a.volume = 1;
            const p = a.play();
            if (p && typeof p.then === "function") {
              p.catch(() => { URL.revokeObjectURL(url); fallback(); });
            }
            return; // playback started — onended/onerror will settle it
          } catch {
            clearTimeout(timer);
            if (stale()) { finish(); return; }
            const quick = Date.now() - started < 8000;
            if (attempt === 0 && quick) continue; // transient blip — retry once
            fallback();
            return;
          }
        }
      })();
    });
  }

  async function handleTurn(text) {
    if (!activeRef.current || busyRef.current) return;
    const clean = (text || "").trim();
    if (!clean) {
      if (mode === "sr") startListening();
      else setState("idle");
      return;
    }
    busyRef.current = true;
    if (mode === "sr") stopRec();
    setTranscript(clean);

    if (SIGNOFF.test(clean)) {
      await playSpeech(FAREWELL);
      doClose();
      return;
    }
    if (APPROVE.test(clean)) {
      window.dispatchEvent(new Event("ph:voice-approve-latest"));
      await playSpeech("Saved.");
      busyRef.current = false;
      if (activeRef.current) afterReply();
      return;
    }
    if (SKIP.test(clean)) {
      window.dispatchEvent(new Event("ph:voice-skip-latest"));
      await playSpeech("Skipped.");
      busyRef.current = false;
      if (activeRef.current) afterReply();
      return;
    }

    setState("thinking");
    let reply = "";
    try {
      reply = await onSubmit(clean);
    } catch {
      reply = "Sorry — something went wrong reaching me. Let's try that again.";
    }
    if (!activeRef.current) return;
    if (reply) await playSpeech(reply);
    busyRef.current = false;
    if (!activeRef.current) return;
    afterReply();
  }

  // After a reply finishes, decide what to do next. Both modes keep the
  // conversation going hands-free: SR mode re-listens; push-to-talk mode
  // auto-resumes recording with VAD so the user just keeps talking.
  function afterReply() {
    if (mode === "sr") { startListening(); return; }
    setTranscript("");
    if (activeRef.current) startRecording();
  }

  // ---------------------------------------------------------------------
  // SR (Web Speech) path — desktop Chrome / Android Chrome.
  // ---------------------------------------------------------------------
  function startListening() {
    if (!activeRef.current) return;
    if (recRef.current) stopRec();
    busyRef.current = false;
    fatalRef.current = false;
    clearSilence();
    setError("");
    setTranscript("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      setState("idle");
      return;
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    let said = "";

    const armSilence = () => {
      clearSilence();
      silenceRef.current = setTimeout(() => {
        silenceRef.current = null;
        const text = said.trim();
        if (text) handleTurn(text);
      }, SILENCE_MS);
    };

    r.onresult = (e) => {
      if (recRef.current !== r) return;
      let finalT = "";
      let interim = "";
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalT += `${t} `; else interim += t;
      }
      said = finalT.trim();
      setTranscript((`${said} ${interim}`).trim());
      if (said || interim.trim()) armSilence();
    };
    r.onerror = (ev) => {
      if (recRef.current !== r) return;
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        fatalRef.current = true;
        setError("Microphone permission denied. Allow the mic, then tap the orb.");
      } else if (ev.error !== "no-speech" && ev.error !== "aborted") {
        setError(`Mic error: ${ev.error}`);
      }
    };
    r.onend = () => {
      if (recRef.current !== r) return;
      if (!activeRef.current || busyRef.current || fatalRef.current) return;
      clearSilence();
      const text = said.trim();
      if (text) handleTurn(text);
      else startListening();
    };
    recRef.current = r;
    setTimeout(() => {
      if (recRef.current !== r || !activeRef.current) return;
      try { r.start(); setState("listening"); }
      catch {
        recRef.current = null;
        setTimeout(() => { if (activeRef.current) startListening(); }, 300);
      }
    }, 120);
  }

  // ---------------------------------------------------------------------
  // Push-to-talk path — iOS Safari (and any browser without SR).
  // Continuous & hands-free via voice-activity detection.
  // ---------------------------------------------------------------------

  // Build (or reuse) the Web Audio graph used to measure mic loudness. Kept
  // alive for the whole session so auto-resume after a reply needs no new user
  // gesture (iOS would otherwise block a fresh getUserMedia/resume).
  function ensureVadGraph(stream) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    let ctx = audioCtxRef.current;
    if (!ctx) { ctx = new AC(); audioCtxRef.current = ctx; }
    if (ctx.state === "suspended") { ctx.resume().catch(() => {}); }
    if (!analyserRef.current) {
      try {
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        vadSourceRef.current = src;
        analyserRef.current = analyser;
      } catch { return null; }
    }
    return analyserRef.current;
  }

  function stopVad() {
    if (vadRafRef.current) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null; }
  }

  function startVadLoop() {
    const analyser = analyserRef.current;
    if (!analyser) return; // No VAD on this device — manual tap-to-send still works.
    heardSpeechRef.current = false;
    vadSilenceStartRef.current = 0;
    const data = new Uint8Array(analyser.fftSize);
    const recBegin = Date.now();
    let speechStart = 0;
    const tick = () => {
      if (!activeRef.current || mediaRecRef.current?.state !== "recording") return;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      const now = Date.now();
      if (rms > SPEECH_RMS) {
        if (!speechStart) speechStart = now;
        if (now - speechStart >= MIN_SPEECH_MS) heardSpeechRef.current = true;
        vadSilenceStartRef.current = 0;
      } else {
        speechStart = 0;
        if (heardSpeechRef.current) {
          if (!vadSilenceStartRef.current) vadSilenceStartRef.current = now;
          else if (now - vadSilenceStartRef.current >= POST_SPEECH_SILENCE_MS) {
            autoStopRef.current = true;
            stopRecording();
            return;
          }
        } else if (now - recBegin >= NO_SPEECH_TIMEOUT_MS) {
          giveUpRef.current = true;
          stopRecording();
          return;
        }
      }
      vadRafRef.current = requestAnimationFrame(tick);
    };
    vadRafRef.current = requestAnimationFrame(tick);
  }

  async function startRecording() {
    if (!activeRef.current || busyRef.current) return;
    setError("");
    setTranscript("");
    try {
      let stream = mediaStreamRef.current;
      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
        // A fresh stream needs a fresh analyser source.
        vadSourceRef.current = null;
        analyserRef.current = null;
      }
      const mime = pickRecorderMime();
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      manualStopRef.current = false;
      autoStopRef.current = false;
      giveUpRef.current = false;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stopVad();
        const type = rec.mimeType || "audio/mp4";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        if (recTimerRef.current) { clearTimeout(recTimerRef.current); recTimerRef.current = null; }
        mediaRecRef.current = null;
        const gaveUp = giveUpRef.current; giveUpRef.current = false;
        const manual = manualStopRef.current; manualStopRef.current = false;
        const auto = autoStopRef.current; autoStopRef.current = false;
        if (!activeRef.current) return;
        if (gaveUp) { setState("idle"); return; }
        const meaningful = (manual || auto || heardSpeechRef.current) && blob.size >= 1200;
        if (!meaningful) {
          // Too little audio — likely a double-tap or a stray noise.
          setState("idle");
          return;
        }
        await transcribeAndHandle(blob);
      };
      mediaRecRef.current = rec;
      rec.start();
      setState("recording");
      ensureVadGraph(stream);
      startVadLoop();
      // Safety cap — auto-stop after MAX_RECORD_MS.
      recTimerRef.current = setTimeout(() => {
        try { rec.state === "recording" && rec.stop(); } catch { /* ignore */ }
      }, MAX_RECORD_MS);
    } catch (e) {
      const msg = (e && (e.name || e.message)) || "";
      if (/NotAllowed|Permission/i.test(msg)) {
        setError("Microphone permission denied. Allow the mic in Settings, then tap to talk again.");
      } else {
        setError("Couldn't open the microphone on this device.");
      }
      setState("idle");
    }
  }

  function stopRecording() {
    stopVad();
    const rec = mediaRecRef.current;
    if (!rec) return;
    try { rec.state === "recording" && rec.stop(); } catch { /* ignore */ }
    setState("thinking");
  }

  async function transcribeAndHandle(blob) {
    setState("thinking");
    try {
      const ext = blob.type.includes("webm") ? "webm"
                : blob.type.includes("wav")  ? "wav"
                : "m4a";
      const fd = new FormData();
      fd.append("audio", blob, `speech.${ext}`);
      const r = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (r.data && r.data.text || "").trim();
      if (!text) {
        // Heard speech but couldn't make it out (noise / clipped). Keep the
        // hands-free conversation alive by re-listening once or twice before
        // giving up, so the user doesn't have to re-tap on a single bad parse.
        if (mode === "push" && activeRef.current && emptyTurnsRef.current < 2) {
          emptyTurnsRef.current += 1;
          startRecording();
        } else {
          emptyTurnsRef.current = 0;
          setState("idle");
        }
        return;
      }
      emptyTurnsRef.current = 0;
      await handleTurn(text);
    } catch {
      setError("Transcription failed. Tap the orb to retry.");
      setState("idle");
    }
  }

  // The big orb is the action button on push-to-talk devices.
  async function handleOrbTap() {
    if (!activeRef.current) return;
    if (mode === "sr") return; // SR auto-listens; orb is decorative.
    // Always make sure audio is unlocked on this very tap (re-arming is cheap
    // and protects us from iOS quirks where the unlock can expire).
    await unlockAudio();
    if (state === "thinking") return;
    if (state === "recording") { manualStopRef.current = true; stopRecording(); return; }
    if (state === "speaking") { stopSpeaking(); return; } // barge-in
    if (state === "idle" && !busyRef.current) startRecording();
  }

  function stopStream() {
    try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    mediaStreamRef.current = null;
    vadSourceRef.current = null;
    analyserRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
  }

  function doClose() {
    activeRef.current = false;
    busyRef.current = false;
    stopRec();
    stopVad();
    try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch { /* ignore */ }
    if (recTimerRef.current) { clearTimeout(recTimerRef.current); recTimerRef.current = null; }
    stopStream();
    stopSpeaking();
    setState("idle");
    onClose();
  }

  // Open / close lifecycle.
  useEffect(() => {
    if (!open) return undefined;
    activeRef.current = true;
    busyRef.current = false;
    fatalRef.current = false;
    emptyTurnsRef.current = 0;
    // Unlock audio output while we still have a user gesture (the tap that
    // opened the overlay). Without this, iOS Safari blocks every later
    // audio.play() call and Andre silently never speaks.
    unlockAudio();
    let id = null;
    if (mode === "sr") {
      id = setTimeout(() => startListening(), 0);
    } else {
      // Push-to-talk — wait for the user to tap the orb.
      setState("idle");
    }
    return () => {
      activeRef.current = false;
      busyRef.current = false;
      if (id) clearTimeout(id);
      stopRec();
      stopVad();
      try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch { /* ignore */ }
      stopStream();
      stopSpeaking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const isPush = mode === "push";
  const stateLabel = isPush
    ? ({
        idle:      "Tap the orb to talk",
        recording: "Listening\u2026 tap to send",
        thinking:  "Thinking\u2026",
        speaking:  `${assistantName} is speaking\u2026`,
      }[state] || "Tap the orb to talk")
    : ({
        idle:      "Press to listen",
        listening: "Listening\u2026",
        thinking:  "Thinking\u2026",
        speaking:  `${assistantName} is speaking\u2026`,
      }[state] || "");

  const orbState = state === "recording" ? "listening"
                 : state === "idle"      ? "listening"
                 : state;

  return (
    <div className="voice-overlay" data-testid="voice-overlay" role="dialog" aria-label="Voice mode">
      <button
        onClick={doClose}
        className="btn btn-ghost"
        data-testid="voice-close-btn"
        style={{ position: "absolute", top: 24, right: 28 }}
      >
        <X size={18} />
      </button>

      <button
        type="button"
        onClick={isPush ? handleOrbTap : undefined}
        aria-label={isPush ? `Tap to talk to ${assistantName}` : "Voice mode"}
        data-testid={isPush ? "voice-orb-button" : "voice-orb-static"}
        style={{
          background: "transparent", border: "none", padding: 0,
          cursor: isPush ? "pointer" : "default",
          // A subtle pulse halo when recording so you know mic is hot.
          filter: state === "recording" ? "drop-shadow(0 0 24px rgba(78,205,196,0.45))" : "none",
          transition: "filter 280ms ease",
        }}
      >
        <Orb size={220} state={orbState} />
      </button>

      <div
        style={{ marginTop: 36, fontSize: 13, color: "var(--ink-dim)", letterSpacing: "0.18em", textTransform: "uppercase" }}
        data-testid="voice-state-label"
      >
        {stateLabel}
      </div>

      <div
        data-testid="voice-transcript"
        style={{ marginTop: 22, maxWidth: 720, padding: "0 32px", textAlign: "center", color: "var(--ink)", fontSize: 22, lineHeight: 1.5, minHeight: 64 }}
      >
        {error ? (
          <span className="text-dim" style={{ fontSize: 14 }} data-testid="voice-error">{error}</span>
        ) : (
          transcript || (
            <span className="text-dim" style={{ fontSize: 16 }}>
              {isPush ? "Tap the orb, then just talk\u2026"
                      : "Say something to begin\u2026"}
            </span>
          )
        )}
      </div>

      <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--ink-dim)" }}>
        {isPush ? (
          <>
            {"Tap once, then just talk \u2014 I\u2019ll reply and keep listening. Make sure the side switch is "}
            <strong style={{ color: "var(--ink-soft)" }}>off silent</strong>{" "}
            so you can hear {assistantName}.
          </>
        ) : (
          <>
            {"It\u2019s a conversation \u2014 take your time, pause to think. Say "}
            <strong style={{ color: "var(--ink-soft)" }}>{"\u201Csave it\u201D"}</strong>
            {" to approve a draft, or "}
            <strong style={{ color: "var(--ink-soft)" }}>{"\u201Cthanks, that\u2019s all for now\u201D"}</strong>
            {" to finish."}
          </>
        )}
      </div>

      <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
        {isPush && (state === "idle") && !error && (
          <button className="btn btn-primary" onClick={handleOrbTap} data-testid="voice-talk-btn">
            <Mic size={14} /> Tap to talk
          </button>
        )}
        {isPush && state === "recording" && (
          <button className="btn btn-primary" onClick={handleOrbTap} data-testid="voice-send-btn">
            <Square size={12} /> Send now
          </button>
        )}
        {isPush && error && (
          <button className="btn btn-primary" onClick={() => { setError(""); handleOrbTap(); }} data-testid="voice-retry-btn">
            <Mic size={14} /> Try again
          </button>
        )}
        {!isPush && error && supported && (
          <button className="btn" onClick={startListening} data-testid="voice-retry-btn">
            <Mic size={14} /> Try again
          </button>
        )}
        {!isPush && transcript && state === "listening" && (
          <button
            className="btn btn-primary"
            onClick={() => handleTurn(transcript)}
            data-testid="voice-submit-btn"
          >
            <Send size={14} /> Send
          </button>
        )}
        <button className="btn btn-ghost" onClick={doClose} data-testid="voice-cancel-btn">
          End
        </button>
      </div>
    </div>
  );
}
